import React from 'react';
import { ArrowDown, MapPin, Mail } from 'lucide-react';
import { profile, stats } from '../mock';

const HeroBackdrops = () => (
  <>
    <div
      className="absolute inset-0 opacity-[0.07] pointer-events-none"
      style={{
        backgroundImage: 'radial-gradient(rgba(201,169,97,0.5) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }}
    />
    <div
      className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full pointer-events-none"
      style={{
        background: 'radial-gradient(closest-side, rgba(201,169,97,0.18), transparent 70%)',
      }}
    />
  </>
);

const HeroContactLine = ({ email, location }) => (
  <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-white/65">
    <span className="inline-flex items-center gap-2">
      <MapPin size={15} className="text-[#c9a961]" /> {location}
    </span>
    <span className="hidden sm:inline-block w-px h-4 bg-white/20" />
    <a
      href={`mailto:${email}`}
      className="inline-flex items-center gap-2 hover:text-[#c9a961] transition-colors"
    >
      <Mail size={15} className="text-[#c9a961]" /> {email}
    </a>
  </div>
);

const HeroActions = () => (
  <div className="mt-10 flex flex-wrap gap-4">
    <a href="#contact" className="btn-gold inline-flex items-center gap-2">
      Start a conversation
    </a>
    <a
      href="#experience"
      className="inline-flex items-center gap-2 px-6 py-[0.85rem] border border-white/30 text-white hover:border-[#c9a961] hover:text-[#c9a961] transition-colors"
    >
      View experience <ArrowDown size={16} />
    </a>
  </div>
);

const HeroContent = () => (
  <div className="lg:col-span-7">
    <div className="flex items-center gap-3 mb-6">
      <span className="h-px w-10 bg-[#c9a961]" />
      <span className="text-[11px] tracking-[0.36em] uppercase text-[#c9a961]">
        Banking · Capital Markets · QA Leadership
      </span>
    </div>
    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-white">
      {profile.name}
    </h1>
    <p className="mt-5 font-display text-xl md:text-2xl text-[#d8be7f] italic">{profile.title}</p>
    <p className="mt-7 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
      {profile.tagline}
    </p>
    <HeroContactLine email={profile.email} location={profile.location} />
    <HeroActions />
  </div>
);

const HeroPhoto = () => (
  <div className="lg:col-span-5">
    <div className="relative max-w-md mx-auto lg:ml-auto">
      <div className="absolute -inset-3 border border-[#c9a961]/40 translate-x-3 translate-y-3" />
      <div className="relative overflow-hidden bg-[#0c1a2b]">
        <img
          src={profile.photo}
          alt={profile.name}
          className="w-full h-[460px] object-cover object-top transition-transform duration-700 hover:scale-[1.02]"
        />
        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#07111f] to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#c9a961]">
              In practice since
            </div>
            <div className="font-display text-3xl text-white">2013</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#c9a961]">Domain</div>
            <div className="text-sm text-white">Financial Services</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatsStrip = () => (
  <div className="relative mt-20 max-w-7xl mx-auto px-6 lg:px-10">
    <div className="border-t border-b border-white/10 grid grid-cols-2 md:grid-cols-4">
      {stats.map((s, i) => (
        <div
          key={s.label}
          className={`py-7 px-4 text-center md:text-left ${
            i !== 0 ? 'md:border-l border-white/10' : ''
          }`}
        >
          <div className="font-display text-3xl md:text-4xl text-[#c9a961]">{s.value}</div>
          <div className="text-[11px] tracking-[0.24em] uppercase text-white/55 mt-2">
            {s.label}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Hero = () => (
  <section
    id="top"
    className="relative bg-[#07111f] text-white pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
  >
    <HeroBackdrops />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <HeroContent />
      <HeroPhoto />
    </div>
    <StatsStrip />
  </section>
);

export default Hero;
